<script lang="ts" setup>
import { computed, type PropType, ref } from 'vue'
import { filter, find } from 'lodash'
import type { SideNavItem } from '@helm/types/sideNav'
import type { SideNavLocation } from '@helm/types/sideNav'
import useSideNav from '@helm/composables/sideNav'
import type { Nullable } from '@/types/utility'
import BIcon from '@/components/BIcon.vue'
import SmartIcon from '@helm/components/SmartIcon.vue'

const props = defineProps({
    menu: {
        type: String as PropType<SideNavLocation>,
        required: true
    }
})

const { sideNav, isCurrentRoute, getRouteKey } = useSideNav(props.menu)

const expanded = ref(false)

const getCurrent = computed(
    (): Nullable<SideNavItem> => find(sideNav.value, item => isCurrentRoute(item.route)) ?? null
)
const mobileNav = computed((): SideNavItem[] =>
    getCurrent.value
        ? (filter(sideNav.value, (item: SideNavItem) => !isCurrentRoute(item.route)) as SideNavItem[]) || []
        : (sideNav.value as SideNavItem[])
)
const expandedIconClasses = computed(() => {
    const classList = [`h-6`, `w-6`, `!ml-auto`, `origin-center`, `transition-transform`]

    if (expanded.value) {
        classList.push(`rotate-180`)
    }

    return classList
})

const itemClasses = [
    `flex`,
    `flex-row`,
    `items-center`,
    `gap-4`,
    `h-[50px]`,
    `!px-[22px]`,
    `text-primary`,
    `text-base/none`,
    `font-bold`
]

const linkClasses = [...itemClasses, `opacity-40`, `hover:opacity-100`, `transition-opacity`]
</script>

<template>
    <div class="z-50 flex flex-col bg-secondary-50 print:hidden lg:hidden">
        <button
            :class="itemClasses"
            @click="expanded = !expanded">
            <SmartIcon
                v-if="getCurrent"
                :icon="getCurrent.icon"
                class="h-6 w-6" />

            {{ getCurrent ? getCurrent.label : `Menu` }}

            <BIcon
                icon="chevron-down"
                :class="expandedIconClasses" />
        </button>

        <Transition
            enter-active-class="transition-all"
            leave-active-class="transition-all"
            enter-from-class="-translate-y-4 opacity-0"
            leave-to-class="-translate-y-4 opacity-0">
            <div
                v-if="expanded"
                class="relative w-full">
                <ul class="absolute top-0 w-full bg-secondary-50 shadow-lg">
                    <li
                        v-for="item in mobileNav"
                        :key="getRouteKey(item.route)">
                        <a
                            v-if="item.target && typeof item.route === `string`"
                            :href="item.route"
                            :target="item.target"
                            :class="linkClasses">
                            <SmartIcon
                                :icon="item.icon"
                                class="h-6 w-6" />
                            {{ item.label }}
                        </a>
                        <router-link
                            v-else
                            :to="item.route"
                            :class="linkClasses">
                            <SmartIcon
                                :icon="item.icon"
                                class="h-6 w-6" />
                            {{ item.label }}
                        </router-link>
                    </li>
                </ul>
            </div>
        </Transition>
    </div>
</template>
